import React, { useState, useEffect, useCallback } from "react";
import { authFetch, API_BASE } from "../api";
import ViewToggle from "./shared/ViewToggle";
import SearchBar from "./shared/SearchBar";
import WorkspacePicker from "./shared/WorkspacePicker";
import RefreshRatePicker from "./shared/RefreshRatePicker";
import LiveThumb from "./shared/LiveThumb";
import SchedulePanel, { extractAppName } from "./SchedulePanel";
import "./shared/shared.css";
import "./DeviceDashboard.css";

// Persist the user's chosen auto-refresh interval across reloads. We store
// the raw ms value (or the string "off") in localStorage so "Off" survives
// a refresh; a sessionStorage would be lost.
const REFRESH_STORAGE_KEY = "forge-devices-refresh-ms";
function loadRefreshInterval() {
  const raw = localStorage.getItem(REFRESH_STORAGE_KEY);
  if (raw === "off") return null;
  if (raw == null) return 10000; // default: 10s, matches the prior hardcoded value
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 10000;
}

export default function DeviceDashboard({ cmsAuth }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [devices, setDevices] = useState([]);
  const [deviceStatus, setDeviceStatus] = useState({});
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("forge-devices-view") || "card");
  const [search, setSearch] = useState("");
  const [expandedDevice, setExpandedDevice] = useState(null);
  // Screenshot cache-bust ticks on each refresh cycle (or a cap of 3s,
  // whichever is faster) so live thumbnails feel live without hammering
  // the CDN when the user has picked a long refresh interval.
  const [screenshotCacheBuster, setScreenshotCacheBuster] = useState(() => Date.now());
  // Auto-refresh interval, in ms. `null` = Off (no polling).
  const [refreshMs, setRefreshMs] = useState(loadRefreshInterval);

  const handleRefreshChange = useCallback((ms) => {
    setRefreshMs(ms);
    localStorage.setItem(REFRESH_STORAGE_KEY, ms == null ? "off" : String(ms));
  }, []);

  const cmsHeaders = {
    "x-videri-token": cmsAuth.token,
    "x-tenant": cmsAuth.tenant,
    "x-videri-env": cmsAuth.environment || "dev",
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API_BASE}/videri/workspaces`, { headers: cmsHeaders });
        const data = await res.json();
        const ws = data.workspaces || [];
        setWorkspaces(ws);
        const saved = localStorage.getItem("videri-forge-dashboard-workspace");
        const match = ws.find((w) => w.id === saved);
        if (match) setSelectedWorkspace(match);
        else if (ws.length) setSelectedWorkspace(ws[0]);
      } catch (err) {
        setError("Failed to load workspaces");
      }
    })();
  }, []); // eslint-disable-line

  const fetchDeviceData = useCallback(async (workspace) => {
    if (!workspace) return;
    setLoading(true);
    setError(null);
    try {
      const devRes = await authFetch(`${API_BASE}/videri/devices?groupId=${workspace.id}`, { headers: cmsHeaders });
      const devData = await devRes.json();
      const devList = devData.devices || [];
      setDevices(devList);

      const deviceIds = devList.map((d) => d.id || d.deviceId);
      const [statusRes, eventsRes] = await Promise.all([
        authFetch(`${API_BASE}/videri/devices/status`, {
          method: "POST",
          headers: { ...cmsHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({ deviceIds }),
        }),
        authFetch(`${API_BASE}/videri/events?groupId=${workspace.id}`, { headers: cmsHeaders }),
      ]);

      const statusData = await statusRes.json();
      setDeviceStatus(statusData || {});

      const eventsData = await eventsRes.json();
      setEvents(Array.isArray(eventsData) ? eventsData : eventsData.events || []);
    } catch (err) {
      setError("Failed to load device data");
    } finally {
      setLoading(false);
    }
  }, [cmsAuth.token, cmsAuth.tenant]); // eslint-disable-line

  useEffect(() => {
    if (selectedWorkspace) {
      localStorage.setItem("videri-forge-dashboard-workspace", selectedWorkspace.id);
      fetchDeviceData(selectedWorkspace);
    }
  }, [selectedWorkspace, fetchDeviceData]);

  useEffect(() => {
    if (!selectedWorkspace || refreshMs == null) return;
    const interval = setInterval(() => fetchDeviceData(selectedWorkspace), refreshMs);
    return () => clearInterval(interval);
  }, [selectedWorkspace, fetchDeviceData, refreshMs]);

  // Screenshot cache-bust: refresh at min(refreshMs, 3s). Screenshots are
  // direct-to-CDN and cheap, so we want them snappy — but if the user
  // picked "Off" we also pause the screenshots.
  useEffect(() => {
    if (refreshMs == null) return;
    const screenshotTick = Math.min(refreshMs, 3000);
    const interval = setInterval(() => setScreenshotCacheBuster(Date.now()), screenshotTick);
    return () => clearInterval(interval);
  }, [refreshMs]);

  function getDeviceEvents(deviceId) {
    return events.filter((e) => e.canvasesIds && e.canvasesIds.includes(deviceId));
  }

  function getForgeApps(deviceId) {
    const devEvents = getDeviceEvents(deviceId);
    const apps = [];
    for (const evt of devEvents) {
      const name = extractAppName(evt);
      if (name && name !== "Unknown" && !apps.includes(name)) apps.push(name);
    }
    return apps;
  }

  function formatLastSeen(ts) {
    if (!ts) return "Never";
    const diff = Date.now() - new Date(ts).getTime();
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  }

  const onlineCount = devices.filter((d) => {
    const status = deviceStatus[d.id || d.deviceId];
    return d.online || status?.online;
  }).length;

  const filtered = devices.filter((d) =>
    !search || d.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="dd-page">
      <div className="page-header">
        <h2>Devices</h2>
        <div className="page-header-right">
          {devices.length > 0 && (
            <>
              <span className="apps-stat">{devices.length} total</span>
              <span className="apps-stat apps-stat-running">{onlineCount} online</span>
              <span className="apps-stat apps-stat-stopped">{devices.length - onlineCount} offline</span>
            </>
          )}
        </div>
      </div>

      <div className="page-toolbar">
        <div className="page-toolbar-left">
          <WorkspacePicker
            workspaces={workspaces}
            selectedId={selectedWorkspace?.id || null}
            onSelect={(ws) => setSelectedWorkspace(ws)}
            emptyLabel={workspaces.length ? "Select workspace" : "No workspaces available"}
            disabled={!workspaces.length}
          />
          <RefreshRatePicker
            value={refreshMs}
            onChange={handleRefreshChange}
            onManualRefresh={() => {
              if (selectedWorkspace) fetchDeviceData(selectedWorkspace);
              setScreenshotCacheBuster(Date.now());
            }}
            refreshing={loading}
            disabled={!selectedWorkspace}
          />
        </div>
        <div className="page-toolbar-right">
          <SearchBar value={search} onChange={setSearch} placeholder="Search devices..." />
          <ViewToggle view={view} onChange={setView} storageKey="forge-devices-view" />
        </div>
      </div>

      <div className="page-body">
        {error && <div className="page-empty" style={{ color: "var(--color-danger)" }}>{error}</div>}
        {loading && devices.length === 0 && <div className="page-empty">Loading devices...</div>}
        {!loading && devices.length === 0 && !error && <div className="page-empty">No devices in this workspace</div>}

        {view === "table" && filtered.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Status</th>
                <th style={{ width: 80 }}>Live</th>
                <th>Name</th>
                <th>Location</th>
                <th>Orientation</th>
                <th>Product</th>
                <th>Running Apps</th>
                <th>Events</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((device) => {
                const id = device.id || device.deviceId;
                const status = deviceStatus[id];
                const isOnline = device.online || status?.online;
                const forgeApps = getForgeApps(id);
                const devEvents = getDeviceEvents(id);
                const isExpanded = expandedDevice === id;

                return (
                  <React.Fragment key={id}>
                    <tr
                      onClick={() => setExpandedDevice(isExpanded ? null : id)}
                      style={{ cursor: "pointer" }}
                      className={isExpanded ? "dd-table-row-expanded" : ""}
                    >
                      <td data-label="Status">
                        <span className={`status-dot ${isOnline ? "online" : "offline"}`} />
                        {isOnline ? "Online" : "Offline"}
                      </td>
                      <td data-label="Live">
                        {isOnline && (
                          <LiveThumb
                            envId={cmsAuth.environment || "dev"}
                            deviceId={device.deviceId}
                            cacheBuster={screenshotCacheBuster}
                            orientation={device.orientation}
                            className="dd-table-thumb"
                            alt={`${device.name} live`}
                          />
                        )}
                      </td>
                      <td data-label="Name" style={{ fontWeight: 600 }}>
                        <span className="dd-table-chevron">{isExpanded ? "▾" : "▸"}</span>
                        {device.name}
                      </td>
                      <td data-label="Location" style={{ color: "var(--color-text-muted)", fontSize: 12 }}>{device.location || "—"}</td>
                      <td data-label="Orientation" style={{ color: "var(--color-text-muted)", fontSize: 12 }}>{device.orientation || "—"}</td>
                      <td data-label="Product" style={{ color: "var(--color-text-muted)", fontSize: 12 }}>{device.productName || "—"}</td>
                      <td data-label="Running Apps">
                        {forgeApps.length > 0 ? (
                          <div className="dd-app-tags">
                            {forgeApps.map((app) => (
                              <span key={app} className="dd-app-tag">{app}</span>
                            ))}
                          </div>
                        ) : "—"}
                      </td>
                      <td data-label="Events">{devEvents.length}</td>
                    </tr>
                    {isExpanded && (
                      <tr className="dd-table-expand-row">
                        <td colSpan={8}>
                          <SchedulePanel
                            deviceId={id}
                            deviceName={device.name}
                            events={devEvents}
                            cmsAuth={cmsAuth}
                            groupId={selectedWorkspace?.id}
                            onEventsChanged={() => fetchDeviceData(selectedWorkspace)}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
          filtered.map((device) => {
            const id = device.id || device.deviceId;
            const status = deviceStatus[id];
            const isOnline = device.online || status?.online;
            const devEvents = getDeviceEvents(id);
            const forgeApps = getForgeApps(id);
            const isExpanded = expandedDevice === id;

            return (
              <div key={id} className={`dd-device ${isOnline ? "online" : "offline"}`}>
                <div className="dd-device-main" onClick={() => setExpandedDevice(isExpanded ? null : id)} style={{ cursor: "pointer" }}>
                  <div className="dd-device-status">
                    <span className={`dd-dot ${isOnline ? "online" : "offline"}`} />
                  </div>
                  {isOnline && (
                    <LiveThumb
                      envId={cmsAuth.environment || "dev"}
                      deviceId={device.deviceId}
                      cacheBuster={screenshotCacheBuster}
                      orientation={device.orientation}
                      className="dd-device-thumb"
                      alt={`${device.name} live screenshot`}
                    />
                  )}
                  <div className="dd-device-info">
                    <div className="dd-device-name">{device.name}</div>
                    <div className="dd-device-meta">
                      {device.productName && <span>{device.productName}</span>}
                      {device.location && <span>{device.location}</span>}
                      {device.orientation && <span>{device.orientation}</span>}
                    </div>
                    {forgeApps.length > 0 && (
                      <div className="dd-app-tags" style={{ marginTop: 4 }}>
                        {forgeApps.map((app) => (
                          <span key={app} className="dd-app-tag">{app}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="dd-device-right">
                    <span className={`dd-status-label ${isOnline ? "online" : "offline"}`}>
                      {isOnline ? "Online" : "Offline"}
                    </span>
                    {status?.lastSeen && (
                      <span className="dd-last-seen">{formatLastSeen(status.lastSeen)}</span>
                    )}
                    <span className="dd-event-count">{devEvents.length} events</span>
                    <span className="dd-expand-arrow">{isExpanded ? "▾" : "▸"}</span>
                  </div>
                </div>

                {isExpanded && (
                  <SchedulePanel
                    deviceId={id}
                    deviceName={device.name}
                    events={devEvents}
                    cmsAuth={cmsAuth}
                    groupId={selectedWorkspace?.id}
                    onEventsChanged={() => fetchDeviceData(selectedWorkspace)}
                  />
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
